import {
  type ApiBlock,
  type TextractDocument,
} from "amazon-textract-response-parser";
import { useCallback, useEffect, useRef } from "react";

interface IPreviewCanvasComponentProps {
  src: string;
  textractData: TextractDocument;
  templateData: any;
  onSelect: ({ text, line }: { text: string; line: CustomBlock }) => void;
}

export interface CustomBlock {
  left: number;
  top: number;
  width: number;
  height: number;
}

type CustomApiBlock = ApiBlock & {
  Text: string;
};

export default function PreviewCanvasComponent({
  src,
  textractData,
  templateData,
  onSelect,
}: IPreviewCanvasComponentProps) {
  console.log(templateData);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(new Image());

  const extractText = useCallback(
    (line: CustomBlock) => {
      const isBlockWithin = (
        block: ApiBlock,
        left: number,
        top: number,
        width: number,
        height: number
      ) => {
        if (!block.Geometry) return;
        const blockLeft = block?.Geometry?.BoundingBox.Left;
        const blockTop = block?.Geometry?.BoundingBox.Top;
        const blockWidth = block?.Geometry?.BoundingBox.Width;
        const blockHeight = block?.Geometry?.BoundingBox.Height;

        const right = left + width;
        const bottom = top + height;
        const blockRight = blockLeft + blockWidth;
        const blockBottom = blockTop + blockHeight;

        return (
          blockLeft < right &&
          blockRight > left &&
          blockTop < bottom &&
          blockBottom > top
        );
      };

      const getTextFromBlocks = (
        left: number,
        top: number,
        width: number,
        height: number
      ) => {
        const data = textractData
          .listBlocks()
          .filter(
            (block) =>
              block.BlockType === "WORD" &&
              isBlockWithin(block, left, top, width, height)
          )
          .filter((block) => (block as CustomApiBlock).Text.trim() !== "")
          .map((block) => (block as CustomApiBlock).Text)
          .join(" ");
        return data.trim();
      };

      if (textractData?.listBlocks().length > 0) {
        const text = getTextFromBlocks(
          line.left,
          line.top,
          line.width,
          line.height
        ).trim();
        return { text, line };
      }
      return { text: "", line };
    },
    [textractData]
  );

  useEffect(() => {
    if (src != null && textractData != null) {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      const image = imageRef.current;

      const drawImageAndBlocks = () => {
        if (!canvas || !context) return;

        const dpr = window.devicePixelRatio || 1;

        const cssWidth = image.width //* Wscale;
        const cssHeight = image.height //* Hscale;
        // High-resolution canvas
        canvas.width = image.width * dpr;
        canvas.height = image.height * dpr;

        context.scale(dpr, dpr);

        // Draw the image at correct aspect ratio
        context.drawImage(image, 0, 0, cssWidth, cssHeight);

        // Draw template boxes
        templateData.CropImageModel.CropImageModelCollection.forEach((tag: any) => {
          const left = tag.x * cssWidth;
          const top = tag.y * cssHeight;
          const width = tag.imagewidth * cssWidth;
          const height = tag.imageheight * cssHeight;

          context.fillStyle = "rgba(235, 241, 235, 0.3)";
          context.fillRect(left, top, width, height);

          context.strokeStyle = tag.tagtype === "STATIC" ? "blue" : "red";
          context.lineWidth = 6;
          context.strokeRect(left, top, width, height);
        });
      };

      image.src = src;
      image.onload = drawImageAndBlocks;
    }
  }, [
    src,
    textractData,
    onSelect,
    extractText,
    templateData.CropImageModel.CropImageModelCollection,
  ]);

  return <canvas ref={canvasRef} className="block m-auto" />;
}
